import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from '@/components/ui/Icon';
import { Input } from '@/components/ui/Input';
import { DatePickerSingle } from '@/components/ui/DatePickerSingle';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { toast } from '@/utils/toast';
import { useTimezone } from '@/contexts/TimezoneContext';
import { statusService } from '@/services/status.service';
import { AdminChangelog, ChangelogType, UpdateChangelogData } from '@/types/status';

/**
 * Changelog Manager
 *
 * Admin interface for managing changelog entries
 */

const TYPE_OPTIONS = [
  { value: 'feature', label: 'Feature', icon: 'bulb' },
  { value: 'improvement', label: 'Improvement', icon: 'spark' },
  { value: 'fix', label: 'Fix', icon: 'tools' },
  { value: 'security', label: 'Security', icon: 'shield' },
];

const TYPE_BADGE_CONFIG: Record<ChangelogType, { color: string; bgColor: string }> = {
  feature: { color: 'text-green-700', bgColor: 'bg-green-100' },
  improvement: { color: 'text-primary-800', bgColor: 'bg-primary-50' },
  fix: { color: 'text-amber-700', bgColor: 'bg-amber-100' },
  security: { color: 'text-red-700', bgColor: 'bg-red-100' },
};

export const ChangelogManager = () => {
  const queryClient = useQueryClient();
  const { formatDateOnly } = useTimezone();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<AdminChangelog | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    version: '',
    title: '',
    description: '',
    type: 'feature' as ChangelogType,
    isPublic: true,
    publishedAt: '',
  });

  // Fetch changelog entries
  const { data: entries, isLoading } = useQuery({
    queryKey: ['admin-changelog'],
    queryFn: statusService.getAdminChangelog,
    staleTime: 60 * 1000,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: statusService.createChangelogEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-changelog'] });
      queryClient.invalidateQueries({ queryKey: ['changelog'] });
      toast.success('Changelog entry created');
      setCreateModalOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      toast.error(`Failed to create entry: ${error.message}`);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateChangelogData }) =>
      statusService.updateChangelogEntry(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-changelog'] });
      queryClient.invalidateQueries({ queryKey: ['changelog'] });
      toast.success('Changelog entry updated');
      setEditModalOpen(false);
      setSelectedEntry(null);
      resetForm();
    },
    onError: (error: Error) => {
      toast.error(`Failed to update entry: ${error.message}`);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: statusService.deleteChangelogEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-changelog'] });
      queryClient.invalidateQueries({ queryKey: ['changelog'] });
      toast.success('Changelog entry deleted');
      setDeleteConfirmOpen(false);
      setSelectedEntry(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete entry: ${error.message}`);
    },
  });

  const resetForm = () => {
    setFormData({
      version: '',
      title: '',
      description: '',
      type: 'feature',
      isPublic: true,
      publishedAt: '',
    });
  };

  const openEditModal = (entry: AdminChangelog) => {
    setSelectedEntry(entry);
    setFormData({
      version: entry.version || '',
      title: entry.title,
      description: entry.description,
      type: entry.type,
      isPublic: entry.isPublic,
      publishedAt: entry.publishedAt, // Already ISO format
    });
    setEditModalOpen(true);
  };

  const handleCreate = () => {
    if (!formData.version.trim() || !formData.title.trim() || !formData.description.trim() || !formData.publishedAt) {
      toast.error('All fields are required');
      return;
    }

    createMutation.mutate(formData);
  };

  const handleUpdate = () => {
    if (!selectedEntry) return;

    if (!formData.version.trim() || !formData.title.trim() || !formData.description.trim() || !formData.publishedAt) {
      toast.error('All fields are required');
      return;
    }

    updateMutation.mutate({
      id: selectedEntry.id,
      data: formData,
    });
  };

  const handleDelete = () => {
    if (!selectedEntry) return;
    deleteMutation.mutate(selectedEntry.id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name={ICONS.LOADER} size="lg" color="text-gray-400" ariaHidden />
        <span className="ml-2 text-gray-600">Loading changelog...</span>
      </div>
    );
  }

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Changelog Entries</h2>
            <button
              onClick={() => {
                resetForm();
                setCreateModalOpen(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-primary-600-contrast rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Icon name={ICONS.PLUS_CIRCLE} size="sm" color="text-primary-600-contrast" ariaHidden />
              Create Entry
            </button>
          </div>

          {/* Entries List */}
          <div className="space-y-4">
            {entries?.map((entry: AdminChangelog) => {
              const typeConfig = TYPE_BADGE_CONFIG[entry.type];
              return (
                <div
                  key={entry.id}
                  className="p-4 border border-gray-200 rounded-xl hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-mono font-semibold rounded">
                          v{entry.version}
                        </span>
                        <span
                          className={cn(
                            'inline-flex items-center px-2 py-1 rounded-md text-xs font-medium',
                            typeConfig.color,
                            typeConfig.bgColor
                          )}
                        >
                          {TYPE_OPTIONS.find((o) => o.value === entry.type)?.label}
                        </span>
                        <span
                          className={cn(
                            'inline-flex items-center px-2 py-1 rounded-md text-xs font-medium',
                            entry.isPublic
                              ? 'text-green-700 bg-green-100'
                              : 'text-gray-700 bg-gray-100'
                          )}
                        >
                          {entry.isPublic ? 'Public' : 'Private'}
                        </span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-1">{entry.title}</h4>
                      <p className="text-sm text-gray-600 mb-2">{entry.description}</p>
                      <p className="text-xs text-gray-500">
                        Published: {formatDateOnly(entry.publishedAt)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(entry)}
                        className="p-2 text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                        aria-label="Edit entry"
                      >
                        <Icon name={ICONS.EDIT} size="sm" color="text-primary-700" ariaHidden />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedEntry(entry);
                          setDeleteConfirmOpen(true);
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        aria-label="Delete entry"
                      >
                        <Icon name={ICONS.TRASH} size="sm" color="text-red-600" ariaHidden />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {(!entries || entries.length === 0) && (
            <p className="text-center text-gray-500 py-8">No changelog entries</p>
          )}
        </div>
      </div>

      {/* Create Modal */}
      <Modal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        title="Create Changelog Entry"
        footer={
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setCreateModalOpen(false)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={createMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-primary-600-contrast rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Version"
            value={formData.version}
            onChange={(e) => setFormData({ ...formData, version: e.target.value })}
            placeholder="1.2.3"
            required
          />

          <Input
            label="Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Brief summary of changes"
            required
          />

          <Textarea
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Detailed description of changes"
            rows={4}
            required
          />

          <Select
            label="Type"
            options={TYPE_OPTIONS}
            value={formData.type}
            onValueChange={(value) => setFormData({ ...formData, type: value as ChangelogType })}
          />

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isPublic}
                onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">Public (visible to users)</span>
            </label>
          </div>

          <DatePickerSingle
            label="Published Date"
            value={formData.publishedAt}
            onChange={(isoDate) => setFormData({ ...formData, publishedAt: isoDate })}
          />
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        title="Edit Changelog Entry"
        footer={
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setEditModalOpen(false)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdate}
              disabled={updateMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-primary-600-contrast rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Version"
            value={formData.version}
            onChange={(e) => setFormData({ ...formData, version: e.target.value })}
            placeholder="1.2.3"
            required
          />

          <Input
            label="Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Brief summary of changes"
            required
          />

          <Textarea
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Detailed description of changes"
            rows={4}
            required
          />

          <Select
            label="Type"
            options={TYPE_OPTIONS}
            value={formData.type}
            onValueChange={(value) => setFormData({ ...formData, type: value as ChangelogType })}
          />

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isPublic}
                onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">Public (visible to users)</span>
            </label>
          </div>

          <DatePickerSingle
            label="Published Date"
            value={formData.publishedAt}
            onChange={(isoDate) => setFormData({ ...formData, publishedAt: isoDate })}
          />
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete Changelog Entry"
        footer={
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setDeleteConfirmOpen(false)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        }
      >
        <p className="text-gray-700">
          Are you sure you want to delete this changelog entry? This action cannot be undone.
        </p>
        {selectedEntry && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm font-semibold text-gray-900">{selectedEntry.title}</p>
            <p className="text-xs text-gray-500">Version {selectedEntry.version}</p>
          </div>
        )}
      </Modal>
    </>
  );
};
